import * as React from "react";

import { Box, makeStyles, Typography } from "@material-ui/core";
import * as Sentry from "@sentry/browser";
import { useMutation } from "react-query";
import * as Redux from "react-redux";

import {
  UIFieldMap,
  useMerchantModel,
  useRunOnce,
  useTranslation,
  useUiFieldMap,
} from "client/hooks";
import { logAmplitudeEvent } from "client/lib/amplitude";
import { MerchantFieldMapper } from "client/lib/apt-merchant";
import { UserService } from "client/services";
import { errorActions, formCompletionActions } from "client/store";

import { FormBuilder } from "../FormBuilder";

const useStyles = makeStyles(() => ({
  heading: {
    fontSize: `${20 / 16}rem`,
  },
}));
export type CustomerSupportFormFieldUiMap = MerchantFieldMapper<UIFieldMap>;

const uiFieldMap: CustomerSupportFormFieldUiMap = {
  customerSupport: {
    customerSupportEmail: { width: 12 },
    customerSupportPhone: { width: 12 },
    customerSupportWebsite: { width: 12 },
  },
};

export const CustomerSupportForm: React.FC = () => {
  const { merchant, formatMerchantForCustomerSupportInfo } = useMerchantModel();
  const dispatch = Redux.useDispatch();
  const t = useTranslation();
  const classes = useStyles();

  useRunOnce(() =>
    logAmplitudeEvent(
      "REDESIGN_ADD_CUSTOMER_SERVICE",
      "loaded_customer_support_form"
    )
  );

  const { mutate: submitCustomerSupportInfo } = useMutation(
    UserService.completeCustomerServiceForm,
    {
      onError: (err: Error) => {
        dispatch(errorActions.setError(err.message));
      },
      onSuccess: ({ validation_errors, formCompletedState }) => {
        if (validation_errors) {
          dispatch(errorActions.setFieldErrorMap(validation_errors));
        } else if (formCompletedState) {
          dispatch(formCompletionActions.setFormCompletion(formCompletedState));
          logAmplitudeEvent(
            "REDESIGN_ADD_CUSTOMER_SERVICE",
            "submitted_customer_service_info"
          );
        } else {
          dispatch(errorActions.setError("Unexpected error"));
          Sentry.captureException(
            new Error(
              `Unexpected error submitting customer service info, merchantId: ${
                merchant.merchantAccount?.merchantId ?? "N/A"
              }, mlsProspectRef: ${
                merchant.merchantAccount?.mlsProspectRef ?? "N/A"
              }`
            )
          );
        }
      },
    }
  );

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = formatMerchantForCustomerSupportInfo();

    submitCustomerSupportInfo(data);
  };

  const { orderedUiFieldMap } = useUiFieldMap(uiFieldMap);

  return (
    <>
      <Box mb={2}>
        <Typography variant="h6" className={classes.heading}>
          <strong>{t("uiCustomerSupportHeading")}</strong>
        </Typography>
      </Box>
      <Box mb={3}>
        <Typography variant="body2">
          {t("uiCustomerSupportSubtitle")}
        </Typography>
      </Box>
      <Box maxWidth={468}>
        <FormBuilder
          fieldSet={merchant.customerSupport}
          onSubmit={handleSubmit}
          submitLabel={"Submit"}
          uiFieldMap={orderedUiFieldMap.customerSupport}
        />
      </Box>
    </>
  );
};
